import { ChangeDetectorRef, Component, OnInit, signal, viewChild, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router, RouterEvent, Event } from '@angular/router';
import { DynamicContentService } from '../../services/dynamic-content/dynamic-content.service';
import { Subscription, combineLatest, distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs';
import { EditTournamentComponent } from 'src/app/game/component/edit-tournament/edit-tournament.component';
import { UpdateService } from '../../services/update/update.service';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { FeatureService } from 'src/app/core/firebase/feature.service';
import AppLauncherElectron from 'src/app/plugins/app-launcher.plugin';
import { isElectron, isMacOS } from 'src/app/shared/utils/platform.utils';
import { ResizeService } from '../../services/resize/resize.service';
import { FreePlayLobbyService } from 'src/app/core/firebase/free-play.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { DailyBonusService } from 'src/app/core/firebase/dailyBonus.service';
import { SchedulerService } from '../../services/scheduler/scheduler.service';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { animate, style, transition, trigger } from '@angular/animations';
import { SopoMenuService } from '../../services/sopoMenu/sopo-menu.service';
import { LobbyService } from 'src/app/lobby/pages/lobby-home/lobby.service';
import { HomeLobbyStateDispatcher } from 'src/app/core/state/lobby/homeLobby.dispatcher';
import { HomeLobbyComponentId } from 'src/app/core/state/lobby/homeLobby.state';
import { TwitterPixelService } from '../../services/twitter-pixel/twitter-pixel.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    animations: [
        trigger('roll', [
            transition(':enter', [
                style({
                    transform: 'translateY(100%)',
                    opacity: 0,
                }),
                animate('0.3s ease-in-out'),
            ]),
            transition(':leave', [
                style({
                    position: 'absolute',
                    top: '0',
                    right: 0,
                }),
                animate('0.3s ease-in-out', style({
                    position: 'absolute',
                    top: '0',
                    right: 0,
                    transform: 'translateY(-100%)',
                    opacity: 0,
                })),
            ])
        ])
    ],
    standalone: false
})
export class HeaderComponent implements OnInit {

    private goldSubscription: Subscription;
    private sweepbuckSubscription: Subscription;
    private jackpotTicketSubscription: Subscription;
    private routerSubscription: Subscription;
    private sopoMenuViewContainerRef = viewChild('sopoMenu', { read: ViewContainerRef });
    private isInHomeSubscription: Subscription;
    isInHome: boolean = true;
    selected: '' | 'home' | 'shop' | 'social' | 'cashier' | 'profile' | 'rewards' | 'points' | 'crypto' |'options' | 'tournamentLobby' | 'club' | 'inGame' = 'home';
    activatedComponent = toSignal(
        this.homeLobbyDispatcher.getActivatedComponent().pipe(map(c => c.componentId)),
        { initialValue: 'lobby-home' as HomeLobbyComponentId }
    );
    isDesktop = signal(false);
    golds = signal([{ id: 0, value: 0 }]);
    sweepbucks = signal([{ id: 0, value: 0 }]);
    jackpotTickets = signal([{ id: 0, value: 0 }]);
    isMacOS = signal(isMacOS());

    sweepbuckEnabled = signal(false);
    isUpdateAvailable = toSignal(this.updateService.isUpdateAvailable(), { initialValue: false });
    tooltips = signal({
        gold: 'Gold is used for freeplay games.<br />Get more in the shop!',
        sweepsbuck: 'Sweeps Coins are used in Sweepstakes games.<br />Get more in Gold packages in the shop!',
        jackpotTicket: 'Jackpot Tickets are used as entry vouchers for Sweepstakes Games.<br />Redeem XP for more!',
    })

    constructor(
        private router: Router,
        private dynamicContentService: DynamicContentService,
        private sopoPopupService: SopoPopupService,
        private ref: ChangeDetectorRef,
        private featureService: FeatureService,
        private resizeService: ResizeService,
        private freeplayService: FreePlayLobbyService,
        private dailyBonusService: DailyBonusService,
        private schedulerService: SchedulerService,
        private profileDispatcher: ProfileStateDispatcher,
        protected updateService: UpdateService,
        private sopoMenuService: SopoMenuService,
        private lobbyService: LobbyService,
        private homeLobbyDispatcher: HomeLobbyStateDispatcher,
        private twitterPixelService: TwitterPixelService,
    ) { }

    ngOnInit() {
        this.sopoMenuService.sopoMenuViewContainerRef = this.sopoMenuViewContainerRef();
        this.isDesktop.set(this.featureService.isDesktop);
        this.sweepbuckEnabled.set(this.featureService.sweepbucks);

        if (this.isDesktop()) this.initSubscriptions();

        this.checkActiveTab(this.router.url);
        this.routerSubscription = this.router.events.pipe(
            filter((event: Event | RouterEvent) => event instanceof NavigationEnd),
            map((event: RouterEvent) => { return event.url })
        ).subscribe(route => {
            this.checkActiveTab(route);
        });
        this.updateService.checkForUpdates();
        this.isInHomeSubscription = this.lobbyService.getIsInHomeObservable().subscribe(isInHome => this.isInHome = isInHome);

        if (this.featureService.sweepbucks) {
            this.lobbyService.setActiveLobbyMenu('sweeps');
        } else {
            this.lobbyService.setActiveLobbyMenu('clubs');
        }
    }

    ionViewDidEnter() {
        this.resizeService.manualResize();
    }

    isInLobbyHome(): boolean {
        return (this.isInHome && this.activatedComponent() !== 'club-list') || this.selected === 'social';
    }

    showSubheader(): boolean {
        return this.selected === 'home' || this.selected === 'tournamentLobby' || this.selected === 'club' || this.selected === 'social';
    }

    checkActiveTab(currentUrl: string) {
        switch (true) {
            case currentUrl.includes('lobby/home'):
            case currentUrl === '/lobby':
            case currentUrl === '/':
                this.selected = 'home';
                break;
            case currentUrl.includes('/profile'):
                this.selected = 'profile';
                break;
            case currentUrl.includes('/shop-home'):
                this.selected = 'shop';
                break;
            case currentUrl.includes('/social'):
                this.selected = 'social';
                break;
            case currentUrl.includes('/cashier'):
                this.selected = 'cashier';
                break;
            case currentUrl.includes('/rewards'):
                this.selected = 'rewards';
                break;
            case currentUrl.includes('/points'):
                this.selected = 'points';
                break;
            case currentUrl.includes('/crypto'):
                this.selected = 'crypto';
                break;
            case currentUrl.includes('/options'):
                this.selected = 'options';
                break;
            case currentUrl.includes('/tournament'):
                this.selected = 'tournamentLobby';
                break;
            case currentUrl.includes('/club'):
                this.selected = 'club';
                break;
            case currentUrl.includes('/game'):
                this.selected = 'inGame';
                break;
            default:
                this.selected = 'home';
        }
        this.ref.detectChanges();
    }

    async confirmNavigation(): Promise<boolean> {
        const url = this.router.url;
        if (url.includes('tournament-registration') && url.includes('SOPO')) {
            if (this.sopoPopupService.getOpenedDialogs('app') > 0 && this.sopoPopupService.isDialogOpen('app', EditTournamentComponent)) return await this.sopoPopupService
                .promptForConfirmation('app', {
                    data: {
                        body: `You have unsaved changes. Do you want to leave this page?`,
                        okText: `Yes`,
                        cancelText: 'No'
                    }
                })
        }
        if (url.includes('/tournament-queue')) {
            const confirmation = await this.freeplayService.navigateAwayFromJackpotQueue();
            if (!confirmation) return;
        }
        return true;
    }

    showCertificate() {
        this.sopoMenuService.closeMenu();
        this.dynamicContentService.openCertificatePreview();
    }

    openLink(link: string) {
        this.twitterPixelService.appDownloadClicked();
        if (isElectron()) AppLauncherElectron.openUrl({ url: link });
        else window.open(link, '_blank');
    }

    toggleMenu() {
        this.sopoMenuService.toggleMenu();
    }

    private initSubscriptions() {
        //Animations Subscriptions -START-

            this.goldSubscription = this.dailyBonusService.goldAnimatorObs.pipe(
                withLatestFrom(this.dailyBonusService.goldChangeDisabledObs)
            ).subscribe(([result, goldChangeDisabled]) => {
                const task = async () => {
                    await this.setCurrencyCount({
                        addedAmount: result.amount,
                        animation: true,
                        changeDisabled: goldChangeDisabled
                    }, result.source, 'gold');
                    this.dailyBonusService.enableGoldChange(result.manipulationId);
                };
                this.schedulerService.addTask({task, taskType: 'gold'});
            });

            const goldSub = combineLatest([
                this.profileDispatcher.getProfileState(),
                this.dailyBonusService.goldChangeDisabledObs
            ]).pipe(
                filter(([_, goldChangeDisabled]) => goldChangeDisabled === false),
                map(([userData, _]) => userData),
                filter(userdata => userdata !== null && userdata.profileData !== null),
                map(userdata => userdata.profileData!.golds),
                distinctUntilChanged(),
            ).subscribe(golds => {
                this.dailyBonusService.animateGift(golds, 'gold', true);
            });

            this.goldSubscription.add(goldSub);

            if (this.sweepbuckEnabled()) {
                this.sweepbuckSubscription = this.dailyBonusService.sweepbuckAnimatorObs.pipe(
                    withLatestFrom(this.dailyBonusService.sweepbuckChangeDisabledObs)
                ).subscribe(([result, sweepbuckChangeDisabled]) => {
                    const task = async () => {
                        await this.setCurrencyCount({
                            addedAmount: result.amount,
                            animation: true,
                            changeDisabled: sweepbuckChangeDisabled
                        }, result.source, 'sweepbuck');
                        this.dailyBonusService.enableSweepbuckChange(result.manipulationId);
                    };
                    this.schedulerService.addTask({ task, taskType: 'sweepbuck'});
                });

                const sweepbuckSub = combineLatest([
                    this.profileDispatcher.getProfileState(),
                    this.dailyBonusService.sweepbuckChangeDisabledObs
                ])
                .pipe(
                    filter(([_, sweepbuckChangeDisabledObs]) => sweepbuckChangeDisabledObs === false),
                    map(([userData, _]) => userData),
                    filter(userdata => userdata !== null && userdata.profileData !== null),
                    map(userdata => userdata.profileData!.sweepbucks),
                    distinctUntilChanged(),
                ).subscribe(sweepbucks => {
                    this.dailyBonusService.animateGift(sweepbucks, 'sweepbuck', true);
                });
                this.sweepbuckSubscription.add(sweepbuckSub);

                this.jackpotTicketSubscription = this.dailyBonusService.jackpotAnimatorObs.pipe(
                    withLatestFrom(this.dailyBonusService.jackpotTicketChangeDisabledObs)
                ).subscribe(([result, jackpotTicketChangeDisabled]) => {
                    const task = async () => {
                        await this.setCurrencyCount({
                            addedAmount: result.amount,
                            animation: true,
                            changeDisabled: jackpotTicketChangeDisabled
                        }, result.source, 'jackpotTicket');
                        this.dailyBonusService.enableJackpotTicketChange(result.manipulationId);
                    };
                    this.schedulerService.addTask({ task, taskType: 'jackpotTicket'});
                });

                const jackpotSub = combineLatest([
                    this.profileDispatcher.getProfileState(),
                    this.dailyBonusService.jackpotTicketChangeDisabledObs
                ])
                .pipe(
                    filter(([_, jackpotTicketChangeDisabledObs]) => jackpotTicketChangeDisabledObs === false),
                    map(([userData, _]) => userData),
                    filter(userdata => userdata !== null && userdata.profileData !== null),
                    map(userdata => userdata.profileData!.jackpotTicket),
                    distinctUntilChanged(),
                ).subscribe(jackpotTickets => {
                    this.dailyBonusService.animateGift(jackpotTickets, 'jackpotTicket', true);
                });
                this.jackpotTicketSubscription.add(jackpotSub);
            }

            //Animations Subscriptions -END-
    }

    private async setCurrencyCount(
        options: {
            addedAmount: number,
            animation?: boolean,
            changeDisabled?: boolean
        },
        source: string,
        currencyType: 'gold' | 'sweepbuck' | 'jackpotTicket'
    ) {
        let updatedAmount: number = options.addedAmount || 0;

        let currentAmount;
        switch (currencyType) {
            case 'gold':
                currentAmount = this.golds()[0].value;
                break;
            case 'sweepbuck':
                currentAmount = this.sweepbucks()[0].value;
                break;
            case 'jackpotTicket':
                currentAmount = this.jackpotTickets()[0].value;
                break;
        }

        if (options.changeDisabled) {
            updatedAmount = (currentAmount === undefined ? 0 : currentAmount) + options.addedAmount;
        }
        if (updatedAmount === currentAmount) {
            return;
        }
        const shouldAnimateFlyingGift = options.animation && options.changeDisabled;

        // Animate flying currencies
        if (shouldAnimateFlyingGift && options.addedAmount > 0) {
            try {
                await this.dailyBonusService.animateFlyingGift(source, `homepage-${currencyType}`, currencyType);
            } catch (error) {
                console.error(error);
            }
        }

        const id = Date.now();

        // Update the current amount
        switch (currencyType) {
            case 'gold':
                this.golds.set([{ id, value: updatedAmount }]);
                break;
            case 'sweepbuck':
                this.sweepbucks.set([{ id, value: updatedAmount }]);
                break;
            case 'jackpotTicket':
                this.jackpotTickets.set([{ id, value: updatedAmount}]);
                break;
        }
    }

    ngOnDestroy(): void {
        if (this.goldSubscription) this.goldSubscription.unsubscribe();
        if (this.sweepbuckSubscription) this.sweepbuckSubscription.unsubscribe();
        if (this.jackpotTicketSubscription) this.jackpotTicketSubscription.unsubscribe();
        if (this.routerSubscription) this.routerSubscription.unsubscribe();
        if (this.isInHomeSubscription) this.isInHomeSubscription.unsubscribe();
    }
}
